/**
 * Applies audio gain (amplification) to an AudioBuffer
 *
 * This function multiplies all audio samples by a gain factor to increase or decrease
 * the audio volume. The gain is applied uniformly across all channels.
 *
 * @param audioBuffer - The AudioBuffer to apply gain to
 * @param gainFactor - The gain multiplier (1.0 = no change, 2.0 = double volume, 0.5 = half volume)
 *                     Values > 1.0 amplify the audio, values < 1.0 attenuate it
 * @param preventClipping - Whether to prevent clipping by normalizing if gain would cause overflow
 *
 * @returns A new AudioBuffer with gain applied
 *
 * @example
 * ```typescript
 * // Double the audio volume
 * const amplifiedBuffer = applyAudioGain(originalBuffer, 2.0);
 *
 * // Reduce volume by half
 * const quieterBuffer = applyAudioGain(originalBuffer, 0.5);
 *
 * // Amplify with clipping prevention
 * const safeBuffer = applyAudioGain(originalBuffer, 3.0, true);
 * ```
 */
export const applyAudioGain = (
	audioBuffer: AudioBuffer,
	gainFactor: number,
	preventClipping: boolean = true,
): AudioBuffer => {
	// Create a new AudioBuffer with the same properties
	const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
	const newBuffer = audioContext.createBuffer(
		audioBuffer.numberOfChannels,
		audioBuffer.length,
		audioBuffer.sampleRate,
	);

	// Apply gain to each channel
	for (let channel = 0; channel < audioBuffer.numberOfChannels; channel++) {
		const originalData = audioBuffer.getChannelData(channel);
		const newData = newBuffer.getChannelData(channel);

		// First pass: apply gain and find peak if clipping prevention is enabled
		let peak = 0;
		for (let i = 0; i < originalData.length; i++) {
			const amplifiedSample = originalData[i] * gainFactor;
			newData[i] = amplifiedSample;

			if (preventClipping) {
				peak = Math.max(peak, Math.abs(amplifiedSample));
			}
		}

		// Second pass: normalize if clipping would occur
		if (preventClipping && peak > 1.0) {
			const normalizationFactor = 1.0 / peak;
			for (let i = 0; i < newData.length; i++) {
				newData[i] *= normalizationFactor;
			}
		} else if (!preventClipping) {
			// Clamp values to prevent distortion if clipping prevention is disabled
			for (let i = 0; i < newData.length; i++) {
				newData[i] = Math.max(-1.0, Math.min(1.0, newData[i]));
			}
		}
	}

	return newBuffer;
};

/**
 * Calculates the RMS (Root Mean Square) level of an AudioBuffer
 * This can be used to determine appropriate gain levels
 *
 * @param audioBuffer - The AudioBuffer to analyze
 * @returns The RMS level (0.0 to 1.0)
 */
export const calculateRMSLevel = (audioBuffer: AudioBuffer): number => {
	let sumSquares = 0;
	let sampleCount = 0;

	for (let channel = 0; channel < audioBuffer.numberOfChannels; channel++) {
		const channelData = audioBuffer.getChannelData(channel);
		for (let i = 0; i < channelData.length; i++) {
			sumSquares += channelData[i] * channelData[i];
			sampleCount++;
		}
	}

	return Math.sqrt(sumSquares / sampleCount);
};

/**
 * Calculates an appropriate gain factor to normalize audio to a target RMS level
 *
 * @param audioBuffer - The AudioBuffer to analyze
 * @param targetRMS - The desired RMS level (typically 0.1 to 0.5)
 * @param maxGain - Maximum allowed gain factor to prevent excessive amplification
 * @returns The calculated gain factor
 */
export const calculateNormalizationGain = (
	audioBuffer: AudioBuffer,
	targetRMS: number = 0.2,
	maxGain: number = 10.0,
): number => {
	const currentRMS = calculateRMSLevel(audioBuffer);

	if (currentRMS === 0) {
		return 1.0; // Avoid division by zero for silent audio
	}

	const calculatedGain = targetRMS / currentRMS;
	return Math.min(calculatedGain, maxGain);
};
